<script setup lang="tsx">
import { computed, ref, nextTick, watch } from 'vue'
import http from '@/services/http'
import axios from 'axios'
import type { AddUserReplyRequest, Audit, AuditComment, EditAuditCommentRequest, Response, UserReply } from '@/models/api-generated'
import { formatDate } from '@/services/utils'
import { Ok, Remark, Defect, NoKind, type AuditCommentKind } from '@/models/enums-generated'

const model = defineModel<AuditComment>()
const audit = defineModel<Audit>('audit', { required: true })
const currentUser = sessionMap.user
const canEdit = computed(() => model.value?.author?.id === currentUser.id && audit.value?.isActive)
const canReply = computed(() => audit.value?.isActive)
const emit = defineEmits(['reply-added', 'kind-changed'])

const isEditingComment = ref(false)
const isAddReply = ref(false)
const reply = ref('')
const commentRef = ref<HTMLTextAreaElement | null>(null)
const replyRef = ref<HTMLTextAreaElement | null>(null)
const oldCommentKind = ref<AuditCommentKind | null>(null)

async function editComment() {
	if (!model.value || !model.value.value) return

	const req: EditAuditCommentRequest = {
		commentId: model.value.id,
		value: model.value.value,
		kind: model.value.kind,
	}
	const data = await http.post<Response<AuditComment[]>>('api/audit/editComment', req)
	if (data.status === axios.HttpStatusCode.Ok) {
		isEditingComment.value = false
		emit('kind-changed', { oldKind: oldCommentKind.value, newKind: model.value.kind })
	}
}

async function addReply() {
	if (!model.value || !reply.value) return

	const req: AddUserReplyRequest = {
		commentId: model.value.id,
		value: reply.value,
		auditId: audit.value.id ?? 0,
		methodMetadataId: model.value.methodMetadataId ?? 0,
	}

	const data = await http.post<Response<UserReply>>('api/audit/addReply', req)
	if (data.status === axios.HttpStatusCode.Ok) {
		isAddReply.value = false
		reply.value = ''
		emit('reply-added')
	}
}

const focusTextArea = () => {
	nextTick(() => {
		if (isEditingComment.value && commentRef.value) {
			commentRef.value.focus()
		}

		if (isAddReply.value && replyRef.value) {
			replyRef.value.focus()
		}
	})
}
watch([isEditingComment, isAddReply], focusTextArea)

const GetIconClass = computed(() => {
	if (!model.value) return ''

	switch (model.value.kind) {
		case 'ok':
			return 'bi bi-check-circle text-success'
		case 'remark':
			return 'bi bi-exclamation-circle text-warning'
		case 'defect':
			return 'bi bi-ban text-danger'
		case 'noKind':
			return 'bi bi-chat-right-text text-primary'
		case '':
			return 'bi bi-chat-right-text text-primary'

		default:
			return '' // no icon - vanematel kommentaaridel pole kindi
	}
})

function setCommentKind(kind: AuditCommentKind) {
	if (model.value) {
		if (!oldCommentKind.value) {
			oldCommentKind.value = model.value.kind
		}
		model.value.kind = kind
	}
}

function getCommentKindButtonClasses(kind: AuditCommentKind) {
	const baseClasses = ['btn', 'border', 'me-2', 'shadow-sm']
	const isSelected = model.value?.kind === kind

	switch (kind) {
		case 'ok':
			return [...baseClasses, { 'btn-success': isSelected, 'border-primary': isSelected, 'text-success': !isSelected }]
		case 'remark':
			return [...baseClasses, { 'btn-warning': isSelected, 'border-primary': isSelected, 'text-warning': !isSelected }]
		case 'defect':
			return [...baseClasses, { 'btn-danger': isSelected, 'border-primary': isSelected, 'text-danger': !isSelected }]
		case 'noKind':
			return [...baseClasses, { 'btn-info': isSelected, 'border-primary': isSelected, 'text-primary': !isSelected }]
		default:
			return baseClasses
	}
}
</script>

<template>
	<div class="rounded card w-100 m-2">
		<div class="card-body p-0 m-2" v-if="model">
			<div class="card-title fw-bolder text-center mb-3">
				{{ audit.name }}
			</div>

			<div class="d-grid w-100 mb-3" style="grid-template-columns: 1fr auto 1fr">
				<span class="text-start"><i class="bi bi-calendar me-1"></i>{{ formatDate(model.createdAt) }}</span>

				<div v-if="isEditingComment" class="text-center">
					<button
						:id="'nokind-audit-cmt-btn' + model.id"
						:class="getCommentKindButtonClasses(NoKind)"
						@click="setCommentKind(NoKind)"
						v-one-tooltip="$t('audit.comment')"
					>
						<i class="bi bi-chat-right-text"></i>
					</button>
					<button
						:id="'ok-audit-cmt-btn' + model.id"
						:class="getCommentKindButtonClasses(Ok)"
						@click="setCommentKind(Ok)"
						v-one-tooltip="$t('audit.ok')"
					>
						<i class="bi bi-check-circle"></i>
					</button>
					<button
						:id="'remark-audit-cmt-btn' + model.id"
						:class="getCommentKindButtonClasses(Remark)"
						@click="setCommentKind(Remark)"
						v-one-tooltip="$t('audit.remark')"
					>
						<i class="bi bi-exclamation-circle"></i>
					</button>
					<button
						:id="'defect-audit-cmt-btn' + model.id"
						:class="getCommentKindButtonClasses(Defect)"
						@click="setCommentKind(Defect)"
						v-one-tooltip="$t('audit.defect')"
					>
						<i class="bi bi-ban"></i>
					</button>
				</div>
				<div v-else class="text-center">
					<i :class="GetIconClass" v-one-tooltip="$t(`audit.${model.kind || 'noKind'}`)"></i>
				</div>

				<span class="badge bg-secondary position-relative text-end" style="justify-self: end">
					<i class="bi bi-person me-1"></i>{{ model.author.label }}
				</span>
			</div>

			<div v-if="isEditingComment">
				<textarea
					:id="'edit-comment-input' + model.id"
					ref="commentRef"
					v-model="model.value"
					class="form-control mb-2"
					rows="3"
					maxlength="500"
					@keydown.enter="editComment()"
				></textarea>
				<div class="text-end">
					<button :id="'save-edit-audit-cmt-btn' + model.id" class="btn btn-success btn-sm me-2" @click="editComment">
						{{ $t('misc.save') }}
					</button>
					<button :id="'cancel-edit-audit-cmt-btn' + model.id" class="btn btn-secondary btn-sm" @click="isEditingComment = false">
						{{ $t('misc.cancel') }}
					</button>
				</div>
			</div>
			<div v-else>
				<p class="card-text text-wrap text-break">{{ model.value }}</p>
				<div v-if="model && canEdit" class="text-end">
					<button
						:id="'edit-audit-cmt-btn' + model.id"
						class="btn btn-primary btn-sm"
						@click="isEditingComment = true"
						v-one-tooltip="$t('misc.edit')"
					>
						<i class="bi bi-pencil-square"></i> {{ $t('misc.edit') }}
					</button>
				</div>
			</div>

			<div v-if="model.userReplies?.length" class="my-3">
				<div v-for="reply in model.userReplies" :key="reply.id" class="border rounded p-2 mb-2">
					<div class="mb-2">
						<i class="bi bi-chat-right-text me-1 text-primary"></i>
						<span class="text-break" style="white-space: normal; word-wrap: break-word">{{ reply.value }}</span>
					</div>
					<div class="d-flex justify-content-between text-muted small">
						<div><i class="bi bi-calendar me-1"></i>{{ formatDate(reply.createdAt) }}</div>
						<div><i class="bi bi-person me-1"></i>{{ reply.author.label }}</div>
					</div>
				</div>
			</div>

			<div class="text-center w-100 my-3" v-if="model && !canEdit && canReply">
				<button
					:id="'add-user-reply-btn' + model.id"
					class="btn btn-primary"
					@click="isAddReply = true"
					v-one-tooltip="$t('audit.addUserReply')"
				>
					<i class="bi bi-reply-fill"></i>
				</button>
				<div v-if="isAddReply">
					<textarea
						:id="'add-reply-input' + model.id"
						ref="replyRef"
						v-model="reply"
						class="form-control mb-2"
						rows="3"
						maxlength="500"
						:placeholder="$t('audit.addUserReply')"
						@keydown.enter="addReply()"
					></textarea>
					<div class="text-end">
						<button :id="'save-user-reply-btn' + model.id" class="btn btn-success btn-sm me-2" @click="addReply">
							{{ $t('misc.save') }}
						</button>
						<button :id="'cancel-user-reply-btn' + model.id" class="btn btn-secondary btn-sm" @click="isAddReply = false">
							{{ $t('misc.cancel') }}
						</button>
					</div>
				</div>
			</div>
		</div>

		<div class="card-body p-0 m-2" v-else>
			<div class="card-title fw-bolder text-center mb-3">{{ audit.name }}</div>
			<p class="card-text text-center">{{ $t('audit.noComments') }}</p>
		</div>
	</div>
</template>

<style lang="sass" scoped>
div, span
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.btn:hover i
    font-size: 1.25em
    transition: font-size 0.2s ease

.btn:hover
    background-color: rgba(0, 0, 0, 0.05)
    transition: background-color 0.2s ease
</style>
